// ============================================
// LEADS.JS — Módulo de gestión de leads/prospectos
// Funciones: listar, buscar, filtrar, agregar y ver detalle
// Es responsive: tarjetas en celular, tabla en desktop
// ============================================

import React, { useState, useEffect } from 'react';
import { BadgeFolio, generarFolio } from './utils/folios';
import { BarraBusqueda, FiltrosRapidos, ContadorResultados, HeaderOrdenable, useOrden, ordenarRegistros } from './utils/filtros';
import { authHeaders } from './utils/storage';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)

// Mapea un lead de la BD al shape que usa este componente
function mapLeadFromApi(l) {
  const capitalizar = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  const estatusMap = {
    nuevo: 'Nuevo',
    en_contacto: 'En contacto',
    calificado: 'Calificado',
    propuesta: 'Propuesta enviada',
    convertido: 'Convertido',
    descartado: 'Descartado',
  };
  const fechaCorta = l.creado_en ? String(l.creado_en).split('T')[0] : '';
  return {
    id: l.id,
    folio: `PRO-2026-${String(l.id).padStart(3, '0')}`,
    nombre: l.nombre || '—',
    empresa: l.empresa || '',
    telefono: l.telefono || '',
    correo: '',
    origen: l.fuente || capitalizar(l.modo || ''),
    estatus: estatusMap[l.estado] || 'Nuevo',
    prioridad: 'Media',
    servicio: l.servicio || '',
    presupuesto: 'No especificado',
    urgencia: '',
    asignado: '—',
    fecha: fechaCorta,
    notas: l.notas || '',
    historial: [
      { fecha: fechaCorta, texto: `Lead recibido${l.fuente ? ' vía ' + l.fuente : ''}`, tipo: 'entrada' },
    ],
  };
}

// ============================================
// COLORES — misma paleta que el dashboard
// ============================================
const colores = {
  principal: '#5c9adb',
  oscuro: '#2f5f9e',
  gris: '#4e5357',
  grisClaro: '#5e666e',
  fondo: '#f3f4f4',
  amarillo: '#dcde74',
  verde: '#1D9E75',
  rojo: '#e05c5c',
};

// ============================================
// CONFIGURACIÓN DE ESTATUS
// Cada estatus tiene su color de borde, fondo y texto
// ============================================
const estatusCfg = {
  'Nuevo':             { borde: '#5c9adb', bg: '#e6f1fb', text: '#1a5fa8' },
  'En contacto':       { borde: '#dcde74', bg: '#fffbe6', text: '#7a6e00' },
  'Calificado':        { borde: '#5cd4b0', bg: '#e8faf5', text: '#1D9E75' },
  'Propuesta enviada': { borde: '#2f5f9e', bg: '#e0e8f8', text: '#2f5f9e' },
  'Convertido':        { borde: '#1D9E75', bg: '#d4f3ea', text: '#1D9E75' },
  'Descartado':        { borde: '#9e9e9e', bg: '#f3f4f4', text: '#5e666e' },
};

// ============================================
// CONFIGURACIÓN DE ORIGEN
// ============================================
const origenCfg = {
  'WhatsApp':       { bg: '#e8f8f3', text: '#1D9E75', label: 'WA'  },
  'Llamada':        { bg: '#eef5ff', text: '#2f5f9e', label: 'Tel' },
  'Web':            { bg: '#e6f1fb', text: '#5c9adb', label: 'Web' },
  'Referido':       { bg: '#fffbe6', text: '#9a8300', label: 'Ref' },
  'Visita directa': { bg: '#fff0f0', text: '#e05c5c', label: 'Dir' },
};

// ============================================
// CONFIGURACIÓN DE PRIORIDAD
// ============================================
const prioridadCfg = {
  'Alta':  { color: '#e05c5c', bg: '#fff0f0' },
  'Media': { color: '#9a8300', bg: '#fffbe6' },
  'Baja':  { color: '#5e666e', bg: '#f3f4f4' },
};

// ============================================
// EQUIPO BEEP
// ============================================
const equipoBeep = ['Carlos V.', 'Laura S.', 'Pedro R.', 'Miguel G.', 'Omar L.'];

// ============================================
// PREGUNTAS DINÁMICAS POR SERVICIO
// Se muestran en el paso 2 del formulario
// ============================================
const preguntasPorServicio = {
  'CCTV': [
    { key: 'camaras',   label: '¿Cuántas cámaras necesita?',   tipo: 'text',   placeholder: 'Ej: 8 cámaras' },
    { key: 'ubicacion', label: '¿Interior o exterior?',         tipo: 'select', opciones: ['Interior', 'Exterior', 'Ambos'] },
    { key: 'cableado',  label: '¿Ya tiene cableado instalado?', tipo: 'select', opciones: ['Sí', 'No', 'Parcialmente'] },
  ],
  'Control de acceso': [
    { key: 'accesos',         label: '¿Cuántos accesos o puertas?', tipo: 'text',   placeholder: 'Ej: 3 accesos' },
    { key: 'credencial',      label: '¿Tipo de credencial?',        tipo: 'select', opciones: ['Tarjeta', 'Huella', 'Pin', 'Facial'] },
    { key: 'integracionCCTV', label: '¿Integración con CCTV?',      tipo: 'select', opciones: ['Sí', 'No', 'Ya tiene CCTV'] },
  ],
  'Redes': [
    { key: 'metros',  label: '¿Metros cuadrados del área?',  tipo: 'text',   placeholder: 'Ej: 500 m²' },
    { key: 'equipos', label: '¿Cuántos equipos conectados?', tipo: 'text',   placeholder: 'Ej: 20 equipos' },
    { key: 'tipoRed', label: '¿Tipo de red preferido?',      tipo: 'select', opciones: ['UTP Cat 6', 'Fibra óptica', 'Mixta'] },
  ],
  'Mantenimiento': [
    { key: 'equiposMant', label: '¿Qué equipos requieren mantenimiento?', tipo: 'text',   placeholder: 'Ej: CCTV Hikvision, red' },
    { key: 'contrato',    label: '¿Tipo de contrato buscado?',             tipo: 'select', opciones: ['Mensual', 'Trimestral', 'Por evento'] },
  ],
  'Otro': [
    { key: 'descripcion', label: '¿Describa el servicio que necesita?', tipo: 'textarea', placeholder: 'Detalle aquí...' },
  ],
};

// ============================================
// FORMULARIO VACÍO
// ============================================
const FORM_VACIO = {
  nombre: '', telefono: '', correo: '', empresa: '',
  origen: 'WhatsApp', prioridad: 'Media', asignado: 'Carlos V.',
  servicio: 'CCTV', presupuesto: '$10k-$50k', urgencia: 'Esta semana', notas: '',
  camaras: '', ubicacion: 'Interior', cableado: 'No',
  accesos: '', credencial: 'Tarjeta', integracionCCTV: 'No',
  metros: '', equipos: '', tipoRed: 'UTP Cat 6',
  equiposMant: '', contrato: 'Mensual', descripcion: '',
};

// ============================================
// LEADS DE EJEMPLO
// 6 leads con distintos estatus para ver el módulo completo
// ============================================
const leadsEjemplo = [
  {
    id: 1,
    folio: 'PRO-2026-001',
    nombre: 'Roberto Sánchez',
    empresa: 'Bodega Central SA',
    telefono: '55 2345 6789',
    correo: 'roberto@bodegacentral.com',
    origen: 'WhatsApp',
    estatus: 'Nuevo',
    prioridad: 'Alta',
    servicio: 'CCTV',
    presupuesto: '$50k-$100k',
    urgencia: 'Esta semana',
    asignado: 'Carlos V.',
    fecha: '2026-03-28',
    notas: 'Quiere cotización urgente para almacén de 2,000 m².',
    historial: [
      { fecha: '2026-03-28', texto: 'Lead recibido vía WhatsApp', tipo: 'entrada' },
    ],
  },
  {
    id: 2,
    folio: 'PRO-2026-002',
    nombre: 'Ana López',
    empresa: 'Clínica Vida',
    telefono: '55 8765 4321',
    correo: 'ana@clinicavida.com',
    origen: 'Web',
    estatus: 'En contacto',
    prioridad: 'Media',
    servicio: 'Control de acceso',
    presupuesto: '$10k-$50k',
    urgencia: 'Este mes',
    asignado: 'Laura S.',
    fecha: '2026-03-25',
    notas: 'Necesita control de acceso para 4 consultorios.',
    historial: [
      { fecha: '2026-03-25', texto: 'Lead recibido vía Web', tipo: 'entrada' },
      { fecha: '2026-03-26', texto: 'Primer llamada realizada, pendiente cotización', tipo: 'nota' },
    ],
  },
  {
    id: 3,
    folio: 'PRO-2026-003',
    nombre: 'Luis Morales',
    empresa: 'Restaurante El Patio',
    telefono: '55 1111 2222',
    correo: 'luis@elpatio.com',
    origen: 'Referido',
    estatus: 'Calificado',
    prioridad: 'Alta',
    servicio: 'Redes',
    presupuesto: '$10k-$50k',
    urgencia: 'Esta semana',
    asignado: 'Carlos V.',
    fecha: '2026-03-22',
    notas: 'Referido por Grupo Nexo. Necesita red para 3 sucursales.',
    historial: [
      { fecha: '2026-03-22', texto: 'Lead referido por Grupo Nexo', tipo: 'entrada' },
      { fecha: '2026-03-23', texto: 'Reunión virtual realizada, interesado', tipo: 'nota' },
      { fecha: '2026-03-24', texto: 'Calificado: presupuesto confirmado', tipo: 'avance' },
    ],
  },
  {
    id: 4,
    folio: 'PRO-2026-004',
    nombre: 'Patricia Ruiz',
    empresa: 'Colegio Montessori',
    telefono: '55 3333 4444',
    correo: 'patricia@montessori.edu.mx',
    origen: 'Llamada',
    estatus: 'Propuesta enviada',
    prioridad: 'Media',
    servicio: 'CCTV',
    presupuesto: '$50k-$100k',
    urgencia: 'Este mes',
    asignado: 'Laura S.',
    fecha: '2026-03-18',
    notas: 'Propuesta enviada para 24 cámaras en instalación escolar.',
    historial: [
      { fecha: '2026-03-18', texto: 'Lead recibido por llamada entrante', tipo: 'entrada' },
      { fecha: '2026-03-19', texto: 'Visita al sitio realizada', tipo: 'nota' },
      { fecha: '2026-03-21', texto: 'Propuesta enviada por correo', tipo: 'avance' },
    ],
  },
  {
    id: 5,
    folio: 'PRO-2026-005',
    nombre: 'Fernando Castro',
    empresa: 'Oficinas Castro Corp',
    telefono: '55 5555 6666',
    correo: 'fcastro@castrocorp.com',
    origen: 'WhatsApp',
    estatus: 'Convertido',
    prioridad: 'Alta',
    servicio: 'Control de acceso',
    presupuesto: '$100k+',
    urgencia: 'Ya',
    asignado: 'Carlos V.',
    fecha: '2026-03-10',
    notas: 'Convertido a cliente. Proyecto iniciado.',
    historial: [
      { fecha: '2026-03-10', texto: 'Lead recibido vía WhatsApp', tipo: 'entrada' },
      { fecha: '2026-03-11', texto: 'Reunión presencial en sus oficinas', tipo: 'nota' },
      { fecha: '2026-03-13', texto: 'Propuesta aprobada por dirección', tipo: 'avance' },
      { fecha: '2026-03-15', texto: 'Contrato firmado — convertido a cliente', tipo: 'avance' },
    ],
  },
  {
    id: 6,
    folio: 'PRO-2026-006',
    nombre: 'Sofía Vega',
    empresa: '',
    telefono: '55 7777 8888',
    correo: 'sofia.vega@gmail.com',
    origen: 'Visita directa',
    estatus: 'Descartado',
    prioridad: 'Baja',
    servicio: 'Mantenimiento',
    presupuesto: 'Menos de $10k',
    urgencia: 'Sin urgencia',
    asignado: 'Pedro R.',
    fecha: '2026-03-20',
    notas: 'Presupuesto muy por debajo del mínimo de servicio.',
    historial: [
      { fecha: '2026-03-20', texto: 'Lead recibido en visita directa a oficina', tipo: 'entrada' },
      { fecha: '2026-03-21', texto: 'Descartado: presupuesto insuficiente', tipo: 'nota' },
    ],
  },
];

// ============================================
// HELPERS — badges que devuelven JSX
// ============================================
function badgeEstatus(estatus) {
  const cfg = estatusCfg[estatus] || { bg: '#f3f4f4', text: '#5e666e' };
  return (
    <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '10px', background: cfg.bg, color: cfg.text, fontWeight: '600', whiteSpace: 'nowrap' }}>
      {estatus}
    </span>
  );
}

function badgeOrigen(origen) {
  const cfg = origenCfg[origen] || { bg: '#f3f4f4', text: '#5e666e', label: origen };
  return (
    <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '8px', background: cfg.bg, color: cfg.text, fontWeight: '700' }}>
      {cfg.label}
    </span>
  );
}

function badgePrioridad(prioridad) {
  const cfg = prioridadCfg[prioridad] || { bg: '#f3f4f4', color: '#5e666e' };
  return (
    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '8px', background: cfg.bg, color: cfg.color, fontWeight: '600' }}>
      {prioridad}
    </span>
  );
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Leads() {

  // --- ESTADOS ---
  const [leads, setLeads]                   = useState([]);
  const [cargandoLeads, setCargandoLeads]   = useState(true);
  const [errorLeads, setErrorLeads]         = useState('');
  const [mostrarForm, setMostrarForm]       = useState(false);
  const [pasoForm, setPasoForm]             = useState(1);           // 1 = datos básicos, 2 = detalles del servicio
  const [busqueda, setBusqueda]             = useState('');
  const [filtroEstatus, setFiltroEstatus]   = useState('Todos');
  const [leadDetalle, setLeadDetalle]       = useState(null);
  const [form, setForm]                     = useState({ ...FORM_VACIO });
  const [nuevaNota, setNuevaNota]           = useState('');

  // Detecta si es celular
  const [width] = useState(window.innerWidth);
  const esCelular = width < 768;
  const [orden, toggleOrden] = useOrden();

  // Carga leads reales desde la BD
  useEffect(() => {
    let cancelado = false;
    (async () => {
      setCargandoLeads(true);
      setErrorLeads('');
      try {
        const r = await fetch(`${API}/api/leads`, { headers: authHeaders() });
        const d = await r.json();
        if (cancelado) return;
        if (d.ok && Array.isArray(d.leads)) {
          setLeads(d.leads.map(mapLeadFromApi));
        } else {
          setErrorLeads('No se pudieron cargar los leads');
        }
      } catch (e) {
        if (!cancelado) setErrorLeads('Error de red cargando leads');
      } finally {
        if (!cancelado) setCargandoLeads(false);
      }
    })();
    return () => { cancelado = true; };
  }, []);

  // Estilo base para inputs y selects
  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1px solid #d0d0d0',
    borderRadius: '8px', fontSize: '13px', background: '#fff',
    boxSizing: 'border-box', outline: 'none', color: colores.gris,
    fontFamily: "'Poppins', sans-serif",
  };
  const labelStyle = { fontSize: '12px', color: colores.grisClaro, display: 'block', marginBottom: '4px' };

  // --- GUARDAR NUEVO LEAD ---
  const handleGuardar = (e) => {
    e.preventDefault();
    const hoy = new Date().toISOString().split('T')[0];
    setLeads([...leads, {
      ...form,
      id: leads.length + 1,
      folio: generarFolio('PRO', leads),
      estatus: 'Nuevo',
      fecha: hoy,
      historial: [{ fecha: hoy, texto: 'Lead creado manualmente', tipo: 'entrada' }],
    }]);
    setForm({ ...FORM_VACIO });
    setPasoForm(1);
    setMostrarForm(false);
  };

  // --- AGREGAR NOTA AL LEAD ---
  const handleAgregarNota = () => {
    if (!nuevaNota.trim()) return;
    const hoy = new Date().toISOString().split('T')[0];
    const entrada = { fecha: hoy, texto: nuevaNota.trim(), tipo: 'nota' };
    const actualizado = { ...leadDetalle, historial: [...leadDetalle.historial, entrada] };
    setLeads(leads.map(l => l.id === actualizado.id ? actualizado : l));
    setLeadDetalle(actualizado);
    setNuevaNota('');
  };

  // --- CAMBIAR ESTATUS DEL LEAD ---
  const handleCambiarEstatus = (nuevoEstatus) => {
    const hoy = new Date().toISOString().split('T')[0];
    const entrada = { fecha: hoy, texto: `Estatus cambiado a: ${nuevoEstatus}`, tipo: 'avance' };
    const actualizado = { ...leadDetalle, estatus: nuevoEstatus, historial: [...leadDetalle.historial, entrada] };
    setLeads(leads.map(l => l.id === actualizado.id ? actualizado : l));
    setLeadDetalle(actualizado);
  };

  // --- FILTRAR LEADS ---
  const leadsFiltrados = leads.filter(l => {
    const q = busqueda.toLowerCase();
    const coincideBusqueda =
      l.nombre.toLowerCase().includes(q) ||
      (l.empresa || '').toLowerCase().includes(q) ||
      l.telefono.includes(busqueda) ||
      l.servicio.toLowerCase().includes(q) ||
      (l.folio || '').toLowerCase().includes(q);
    const coincideEstatus = filtroEstatus === 'Todos' || l.estatus === filtroEstatus;
    return coincideBusqueda && coincideEstatus;
  });
  const leadsOrdenados = ordenarRegistros(leadsFiltrados, orden.campo, orden.dir);

  // ============================================
  // PANTALLA DE DETALLE DEL LEAD
  // ============================================
  if (leadDetalle) {
    const l = leadDetalle;
    const cfg = estatusCfg[l.estatus] || { borde: '#d0d0d0', bg: '#f3f4f4', text: '#5e666e' };
    return (
      <div>
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setLeadDetalle(null)}
            style={{ background: colores.fondo, border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer', color: colores.gris }}
          >
            ← Volver
          </button>
          <BadgeFolio folio={l.folio} />
          <div style={{ fontSize: '16px', fontWeight: '600', color: colores.gris }}>{l.nombre}</div>
          {badgeEstatus(l.estatus)}
          {badgePrioridad(l.prioridad)}
        </div>

        {/* Dos columnas: datos + acciones */}
        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>

          {/* Datos de contacto */}
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Datos de contacto</div>
            {[
              { label: 'Empresa',    val: l.empresa || '-' },
              { label: 'Teléfono',   val: l.telefono },
              { label: 'Correo',     val: l.correo },
              { label: 'Origen',     val: l.origen },
              { label: 'Servicio',   val: l.servicio },
              { label: 'Presupuesto',val: l.presupuesto },
              { label: 'Urgencia',   val: l.urgencia },
              { label: 'Asignado',   val: l.asignado },
              { label: 'Fecha',      val: l.fecha },
            ].map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: '13px', color: colores.grisClaro }}>{d.label}</span>
                <span style={{ fontSize: '13px', color: colores.gris, fontWeight: '500' }}>{d.val}</span>
              </div>
            ))}
            {l.notas && (
              <div style={{ marginTop: '14px', background: colores.fondo, borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: colores.grisClaro, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notas</div>
                <div style={{ fontSize: '13px', color: colores.gris }}>{l.notas}</div>
              </div>
            )}
          </div>

          {/* Acciones rápidas */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Cambiar estatus */}
            <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cambiar estatus</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.keys(estatusCfg).map(est => {
                  const c = estatusCfg[est];
                  const activo = l.estatus === est;
                  return (
                    <button
                      key={est}
                      onClick={() => handleCambiarEstatus(est)}
                      style={{
                        padding: '7px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                        border: `2px solid ${activo ? c.borde : '#e0e0e0'}`,
                        background: activo ? c.bg : '#fff',
                        color: activo ? c.text : colores.grisClaro,
                        transition: 'all 0.15s',
                      }}
                    >
                      {est}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Convertir / Descartar */}
            {l.estatus !== 'Convertido' && l.estatus !== 'Descartado' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <button
                  onClick={() => handleCambiarEstatus('Convertido')}
                  style={{ padding: '12px', borderRadius: '10px', border: 'none', background: '#d4f3ea', color: '#1D9E75', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Convertir a cliente
                </button>
                <button
                  onClick={() => handleCambiarEstatus('Descartado')}
                  style={{ padding: '12px', borderRadius: '10px', border: 'none', background: '#fff0f0', color: colores.rojo, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Descartar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Historial + agregar nota */}
        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Historial de actividad</div>

          {/* Timeline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            {[...l.historial].reverse().map((h, i) => {
              const coloresTipo = { entrada: colores.principal, nota: colores.grisClaro, avance: colores.verde };
              const c = coloresTipo[h.tipo] || colores.grisClaro;
              return (
                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: c, marginTop: '5px', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: colores.gris }}>{h.texto}</div>
                    <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '2px' }}>{h.fecha}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Agregar nota */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
            <div style={{ fontSize: '12px', color: colores.grisClaro, marginBottom: '8px' }}>Agregar nota</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={nuevaNota}
                onChange={e => setNuevaNota(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAgregarNota()}
                placeholder="Escribe una nota y presiona Enter..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={handleAgregarNota}
                style={{ padding: '10px 18px', background: colores.amarillo, border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: colores.gris, whiteSpace: 'nowrap' }}
              >
                Agregar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // PANTALLA PRINCIPAL DE LEADS
  // ============================================
  return (
    <div>

      {/* Encabezado */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '13px', color: colores.grisClaro }}>{leadsOrdenados.length} leads</div>
        <button
          onClick={() => { setMostrarForm(!mostrarForm); setPasoForm(1); setForm({ ...FORM_VACIO }); }}
          style={{ background: colores.amarillo, color: colores.gris, border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
        >
          + Nuevo lead
        </button>
      </div>

      {/* Buscador */}
      <div style={{ marginBottom: '12px' }}>
        <BarraBusqueda value={busqueda} onChange={setBusqueda} placeholder="Buscar por folio, nombre, empresa o servicio..." />
      </div>

      {/* Filtros por estatus — pills */}
      <FiltrosRapidos
        opciones={[
          { value: '', label: 'Todos', count: leads.length },
          { value: 'Nuevo', label: 'Nuevo', count: leads.filter(l => l.estatus === 'Nuevo').length },
          { value: 'En contacto', label: 'En contacto', count: leads.filter(l => l.estatus === 'En contacto').length },
          { value: 'Calificado', label: 'Calificado', count: leads.filter(l => l.estatus === 'Calificado').length },
          { value: 'Propuesta enviada', label: 'Propuesta enviada', count: leads.filter(l => l.estatus === 'Propuesta enviada').length },
          { value: 'Convertido', label: 'Convertido', count: leads.filter(l => l.estatus === 'Convertido').length },
          { value: 'Descartado', label: 'Descartado', count: leads.filter(l => l.estatus === 'Descartado').length },
        ]}
        activo={filtroEstatus === 'Todos' ? '' : filtroEstatus}
        onChange={v => setFiltroEstatus(v || 'Todos')}
      />

      {/* ============================================
          FORMULARIO NUEVO LEAD — 2 pasos
          ============================================ */}
      {mostrarForm && (
        <div style={{ background: '#fff', border: `1px solid ${colores.amarillo}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>

          {/* Indicador de paso */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            {[1, 2].map(p => (
              <React.Fragment key={p}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: '700',
                  background: pasoForm >= p ? colores.amarillo : colores.fondo,
                  color: pasoForm >= p ? colores.gris : colores.grisClaro,
                }}>
                  {p}
                </div>
                {p < 2 && <div style={{ flex: 1, height: '2px', background: pasoForm > 1 ? colores.amarillo : '#e0e0e0', maxWidth: '60px' }} />}
              </React.Fragment>
            ))}
            <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, marginLeft: '8px' }}>
              {pasoForm === 1 ? 'Datos del contacto' : 'Detalles del servicio'}
            </div>
          </div>

          <form onSubmit={pasoForm === 1 ? (e) => { e.preventDefault(); setPasoForm(2); } : handleGuardar}>

            {/* PASO 1 — datos básicos */}
            {pasoForm === 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Nombre *</label>
                  <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required placeholder="Nombre completo" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Empresa</label>
                  <input value={form.empresa} onChange={e => setForm({ ...form, empresa: e.target.value })} placeholder="Nombre de empresa" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Teléfono *</label>
                  <input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} required placeholder="55 1234 5678" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Correo</label>
                  <input value={form.correo} onChange={e => setForm({ ...form, correo: e.target.value })} placeholder="correo@empresa.com" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Origen</label>
                  <select value={form.origen} onChange={e => setForm({ ...form, origen: e.target.value })} style={inputStyle}>
                    {Object.keys(origenCfg).map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Prioridad</label>
                  <select value={form.prioridad} onChange={e => setForm({ ...form, prioridad: e.target.value })} style={inputStyle}>
                    {Object.keys(prioridadCfg).map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Asignar a</label>
                  <select value={form.asignado} onChange={e => setForm({ ...form, asignado: e.target.value })} style={inputStyle}>
                    {equipoBeep.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* PASO 2 — detalles del servicio */}
            {pasoForm === 2 && (
              <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Servicio de interés *</label>
                  <select value={form.servicio} onChange={e => setForm({ ...form, servicio: e.target.value })} required style={inputStyle}>
                    {Object.keys(preguntasPorServicio).map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Presupuesto estimado</label>
                  <select value={form.presupuesto} onChange={e => setForm({ ...form, presupuesto: e.target.value })} style={inputStyle}>
                    {['Menos de $10k', '$10k-$50k', '$50k-$100k', '$100k+', 'No definido'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Urgencia</label>
                  <select value={form.urgencia} onChange={e => setForm({ ...form, urgencia: e.target.value })} style={inputStyle}>
                    {['Ya', 'Esta semana', 'Este mes', 'Sin urgencia'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>

                {/* Preguntas dinámicas según el servicio */}
                {(preguntasPorServicio[form.servicio] || []).map(q => (
                  <div key={q.key}>
                    <label style={labelStyle}>{q.label}</label>
                    {q.tipo === 'text' && (
                      <input value={form[q.key] || ''} onChange={e => setForm({ ...form, [q.key]: e.target.value })} placeholder={q.placeholder} style={inputStyle} />
                    )}
                    {q.tipo === 'select' && (
                      <select value={form[q.key] || q.opciones[0]} onChange={e => setForm({ ...form, [q.key]: e.target.value })} style={inputStyle}>
                        {q.opciones.map(op => <option key={op}>{op}</option>)}
                      </select>
                    )}
                    {q.tipo === 'textarea' && (
                      <textarea value={form[q.key] || ''} onChange={e => setForm({ ...form, [q.key]: e.target.value })} placeholder={q.placeholder} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                    )}
                  </div>
                ))}

                <div style={{ gridColumn: esCelular ? '1' : '1 / -1' }}>
                  <label style={labelStyle}>Notas adicionales</label>
                  <textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} placeholder="Información adicional relevante..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              </div>
            )}

            {/* Botones */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {pasoForm === 2 && (
                <button type="button" onClick={() => setPasoForm(1)} style={{ background: colores.fondo, color: colores.grisClaro, border: '1px solid #d0d0d0', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer' }}>
                  ← Atrás
                </button>
              )}
              <button type="submit" style={{ background: colores.amarillo, color: colores.gris, border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                {pasoForm === 1 ? 'Siguiente →' : 'Guardar lead'}
              </button>
              <button type="button" onClick={() => { setMostrarForm(false); setPasoForm(1); }} style={{ background: 'transparent', color: colores.grisClaro, border: '1px solid #d0d0d0', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ============================================
          LISTA DE LEADS
          En celular: tarjetas apiladas
          En desktop: tabla con columnas
          ============================================ */}

      <div style={{ marginBottom: '12px' }}><ContadorResultados total={leads.length} filtrados={leadsFiltrados.length} /></div>

      {/* VISTA CELULAR — tarjetas */}
      {esCelular ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {leadsOrdenados.map(l => {
            const cfg = estatusCfg[l.estatus] || { borde: '#d0d0d0' };
            return (
              <div
                key={l.id}
                onClick={() => setLeadDetalle(l)}
                style={{ background: '#fff', borderRadius: '12px', padding: '14px 16px', cursor: 'pointer', borderLeft: `4px solid ${cfg.borde}` }}
              >
                <BadgeFolio folio={l.folio} style={{ marginBottom: '6px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris }}>{l.nombre}</div>
                  {badgeEstatus(l.estatus)}
                </div>
                {l.empresa && <div style={{ fontSize: '12px', color: colores.grisClaro, marginBottom: '4px' }}>{l.empresa}</div>}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                  {badgeOrigen(l.origen)}
                  {badgePrioridad(l.prioridad)}
                  <span style={{ fontSize: '12px', color: colores.grisClaro }}>{l.servicio}</span>
                  <span style={{ fontSize: '12px', color: colores.principal, marginLeft: 'auto' }}>{l.asignado}</span>
                </div>
              </div>
            );
          })}
          {leadsOrdenados.length === 0 && (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: colores.grisClaro }}>No hay leads con ese filtro</div>
            </div>
          )}
        </div>
      ) : (
        /* VISTA DESKTOP — tabla */
        <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f4', borderBottom: '1px solid #e0e0e0' }}>
                <HeaderOrdenable label="Folio" campo="folio" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Nombre" campo="nombre" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Empresa" campo="empresa" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Origen" campo="origen" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Servicio" campo="servicio" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Presupuesto" campo="presupuesto" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Asignado" campo="asignado" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Prioridad" campo="prioridad" orden={orden} onOrdenar={toggleOrden} />
                <HeaderOrdenable label="Estado" campo="estatus" orden={orden} onOrdenar={toggleOrden} />
              </tr>
            </thead>
            <tbody>
              {leadsOrdenados.map((l, i) => (
                <tr
                  key={l.id}
                  onClick={() => setLeadDetalle(l)}
                  style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fffde6'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                >
                  <td style={{ padding: '12px 16px' }}><BadgeFolio folio={l.folio} /></td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '500', color: colores.gris }}>{l.nombre}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: colores.grisClaro }}>{l.empresa || '-'}</td>
                  <td style={{ padding: '12px 16px' }}>{badgeOrigen(l.origen)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: colores.grisClaro }}>{l.servicio}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: colores.grisClaro }}>{l.presupuesto}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: colores.grisClaro }}>{l.asignado}</td>
                  <td style={{ padding: '12px 16px' }}>{badgePrioridad(l.prioridad)}</td>
                  <td style={{ padding: '12px 16px' }}>{badgeEstatus(l.estatus)}</td>
                </tr>
              ))}
              {leadsOrdenados.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: colores.grisClaro }}>
                    No hay leads con ese filtro
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Leads;
